import { useState, useEffect, useCallback } from "react";
import { adminCsrfHeaders, adminFetch, setAdminCsrfToken } from "../lib/adminApi";
import { useLocation, Link } from "wouter";
import { PLAN_META } from "@workspace/plans";
import { getPlanBadgeStyle, PLAN_FILTER_OPTIONS } from "@/lib/planBadge";
import {
  LogOut,
  Building2,
  Users,
  FileText,
  TrendingUp,
  Download,
  Search,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  BarChart3,
  DollarSign,
  Percent,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  Link2,
  Activity,
  AlertTriangle,
  Mail,
  Repeat,
  Heart,
  UserCircle2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

type AnyPlanKey = keyof typeof PLAN_META;

interface OrgRow {
  id: number;
  name: string;
  plan: AnyPlanKey;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeCurrentPeriodEnd: string | null;
  createdAt: string;
  division: string | null;
  conference: string | null;
  memberCount: number;
  coachCount: number;
  contractCount: number;
}

interface RevenueData {
  mrr: number;
  arr: number;
  totalOrgs: number;
  payingCustomers: number;
  trialCustomers: number;
  planCounts: Partial<Record<AnyPlanKey, number>>;
  newThisMonth: number;
  newLastMonth: number;
  cancellationsThisMonth: number;
  cancellationsLastMonth: number;
  churnRate: number;
  trialConversionRate: number;
  arpa: number;
  monthlyRevenueTrend: { month: string; revenue: number; invoiceCount: number }[];
  demoClicks30d?: number;
  demoConversions30d?: number;
  demoConversionsTotal?: number;
  demoConversionRate?: number;
  stripeError?: string;
}

type SortKey = "name" | "plan" | "memberCount" | "coachCount" | "contractCount" | "createdAt";
type SortDir = "asc" | "desc";

// Plan colors are derived from the shared planBadge helper so the donut,
// legend, and table badge stay aligned with PLAN_META and never drift.
function planColor(plan: string): string {
  return getPlanBadgeStyle(plan).color;
}

function fmt(n: number, decimals = 0) {
  return n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function planBadgeClass(plan: string) {
  return getPlanBadgeStyle(plan).className;
}

function planBadgeLabel(plan: string) {
  return getPlanBadgeStyle(plan).label;
}

function statusBadge(org: OrgRow) {
  if (org.stripeSubscriptionId) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-300 border border-green-700/50">Active</span>;
  if (org.stripeCustomerId) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-900/40 text-yellow-300 border border-yellow-700/50">Trial</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700/50 text-gray-400 border border-gray-600/50">Free</span>;
}

interface DemoConversionBucket {
  location: "landing_hero" | "landing_final_cta" | "demo_direct" | "unknown" | "total";
  clicks: number;
  signups: number;
  rate: number;
}

const DEMO_BUCKET_LABELS: Record<DemoConversionBucket["location"], string> = {
  landing_hero: "Landing — hero",
  landing_final_cta: "Landing — final CTA",
  demo_direct: "/demo direct",
  unknown: "Unknown / legacy",
  total: "All CTAs",
};

interface DemoFunnelWindow {
  heroClicks: number;
  finalCtaClicks: number;
  leadsCreated: number;
  leadsVerified: number;
}

interface DemoFunnelData {
  window7d: DemoFunnelWindow;
  window30d: DemoFunnelWindow;
  generatedAt: string;
}

const DEMO_FUNNEL_ROWS: { key: keyof DemoFunnelWindow; label: string; hint: string }[] = [
  { key: "heroClicks", label: "Hero CTA clicks", hint: "Landing page hero — Try Live Demo" },
  { key: "finalCtaClicks", label: "Final CTA clicks", hint: "Landing page bottom CTA" },
  { key: "leadsCreated", label: "Demo leads created", hint: "Visitors who entered an email on /demo" },
  { key: "leadsVerified", label: "Demo leads verified", hint: "Leads that completed the 6-digit code gate" },
];

/**
 * Task #498: Demo funnel tile. Shows last-7-day and last-30-day counts of
 * hero/final CTA clicks plus demo leads created vs verified, sourced from
 * `GET /admin/demo/funnel` (owner-only, audited).
 */
function DemoFunnelPanel() {
  const [data, setData] = useState<DemoFunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch("/api/admin/demo/funnel", { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { if (!cancelled) { setData(d as DemoFunnelData); setError(null); } })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="demo-funnel-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-indigo-400" />
        <h3 className="text-sm font-semibold text-white">Demo Funnel</h3>
        <span className="text-xs text-gray-500">— marketing CTA → email gate</span>
      </div>
      {loading && <div className="text-xs text-gray-500">Loading…</div>}
      {error && <div className="text-xs text-red-400">Failed to load: {error}</div>}
      {!loading && !error && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-4 font-medium">Stage</th>
                <th className="py-2 px-3 font-medium text-right">Last 7d</th>
                <th className="py-2 pl-3 font-medium text-right">Last 30d</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_FUNNEL_ROWS.map((row) => (
                <tr key={row.key} className="border-b border-gray-800/60" data-testid={`demo-funnel-row-${row.key}`}>
                  <td className="py-2 pr-4">
                    <div className="text-gray-200">{row.label}</div>
                    <div className="text-[11px] text-gray-500">{row.hint}</div>
                  </td>
                  <td className="py-2 px-3 text-right text-gray-100 tabular-nums" data-testid={`demo-funnel-7d-${row.key}`}>
                    {data.window7d[row.key].toLocaleString()}
                  </td>
                  <td className="py-2 pl-3 text-right text-gray-100 tabular-nums" data-testid={`demo-funnel-30d-${row.key}`}>
                    {data.window30d[row.key].toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DemoConversionPanel() {
  const [data, setData] = useState<{ windowDays: number; buckets: DemoConversionBucket[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`/api/admin/demo/conversions?days=${days}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const buckets = data?.buckets ?? [];
  const rows = buckets.filter((b) => b.location !== "total");
  const total = buckets.find((b) => b.location === "total");

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4" data-testid="demo-conversion-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-400" />
          <h3 className="text-sm font-semibold text-white">Demo Conversion by CTA</h3>
          <span className="text-xs text-gray-500">— last {data?.windowDays ?? days} days</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-gray-800 border border-gray-700 rounded-md text-xs text-gray-200 px-2 py-1 focus:outline-none focus:border-indigo-500"
            data-testid="demo-conversion-window"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
            <option value={365}>365d</option>
          </select>
          <a
            href={`/api/admin/demo/conversions.csv?days=${days}`}
            className="inline-flex items-center gap-1 text-xs bg-gray-800 border border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white px-2.5 py-1 rounded-md"
            data-testid="demo-conversion-csv"
          >
            <Download className="h-3 w-3" />
            CSV
          </a>
        </div>
      </div>
      {loading && <div className="text-xs text-gray-500">Loading…</div>}
      {error && <div className="text-xs text-red-400">Failed to load: {error}</div>}
      {!loading && !error && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-4 font-medium">CTA Location</th>
                <th className="py-2 px-3 font-medium text-right">Clicks</th>
                <th className="py-2 px-3 font-medium text-right">Signups</th>
                <th className="py-2 pl-3 font-medium text-right">Conv. rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.location} className="border-b border-gray-800/60" data-testid={`demo-conv-row-${b.location}`}>
                  <td className="py-2 pr-4 text-gray-200">{DEMO_BUCKET_LABELS[b.location]}</td>
                  <td className="py-2 px-3 text-right text-gray-300 tabular-nums">{b.clicks.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right text-gray-300 tabular-nums">{b.signups.toLocaleString()}</td>
                  <td className="py-2 pl-3 text-right text-indigo-300 tabular-nums">{b.rate}%</td>
                </tr>
              ))}
              {total && (
                <tr className="bg-gray-950/40" data-testid="demo-conv-row-total">
                  <td className="py-2 pr-4 text-white font-semibold">{DEMO_BUCKET_LABELS.total}</td>
                  <td className="py-2 px-3 text-right text-white font-semibold tabular-nums">{total.clicks.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right text-white font-semibold tabular-nums">{total.signups.toLocaleString()}</td>
                  <td className="py-2 pl-3 text-right text-indigo-200 font-semibold tabular-nums">{total.rate}%</td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-gray-500 leading-relaxed">
            Clicks are recorded server-side from the public landing CTAs. Signups are attributed via the <code className="text-gray-400">ah_demo_seen</code> cookie set when the visitor clicked, then read at signup time. <em>Unknown</em> covers legacy cookies issued before per-CTA attribution; <em>/demo direct</em> covers visitors who skipped the marketing CTAs.
          </p>
        </div>
      )}
    </div>
  );
}

interface SalesPresenterStatus {
  active: boolean;
  label: string | null;
  expiresAt: string | null;
}

interface IssuedLink {
  url: string;
  expiresAt: string;
  label: string;
  accessHours: number;
  copied: boolean;
}

const ACCESS_DURATION_OPTIONS: { hours: number; label: string }[] = [
  { hours: 2, label: "2 hours" },
  { hours: 4, label: "4 hours" },
  { hours: 8, label: "8 hours" },
  { hours: 24, label: "24 hours" },
  { hours: 48, label: "2 days" },
  { hours: 168, label: "7 days" },
];

async function copyToClipboardWithFallback(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to execCommand fallback
    }
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function SalesPresenterPanel() {
  const [status, setStatus] = useState<SalesPresenterStatus | null>(null);
  const [label, setLabel] = useState("");
  const [teamLabels, setTeamLabels] = useState("");
  const [accessHours, setAccessHours] = useState(8);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [links, setLinks] = useState<IssuedLink[]>([]);
  const [showHelp, setShowHelp] = useState(false);

  async function refresh() {
    try {
      const r = await fetch("/api/admin/sales-presenter", { credentials: "include" });
      if (r.ok) setStatus(await r.json() as SalesPresenterStatus);
    } catch { /* ignore */ }
  }
  useEffect(() => { refresh(); }, []);

  async function postAdmin(path: string, body?: unknown) {
    return adminFetch(path, {
      method: "POST",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async function issue() {
    setBusy(true); setMsg(null);
    try {
      const r = await postAdmin("/api/admin/sales-presenter/issue", {
        label: label.trim() || "presenter",
        accessHours,
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({} as { error?: string }));
        setMsg(`Issue failed: ${b.error ?? r.status}`);
        return;
      }
      const data = await r.json() as { startUrl: string; expiresAt: string; accessHours?: number };
      setMsg(
        `Presenter cookie set on this browser (${data.accessHours ?? accessHours}h access). Click "Open demo" below — expires ${new Date(data.expiresAt).toLocaleString()}.`,
      );
      await refresh();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Network error");
    } finally { setBusy(false); }
  }
  async function stop() {
    setBusy(true); setMsg(null);
    try {
      const r = await postAdmin("/api/admin/sales-presenter/stop");
      if (r.ok) { setMsg("Sales presenter cookie cleared."); await refresh(); }
      else {
        const b = await r.json().catch(() => ({} as { error?: string }));
        setMsg(`Stop failed: ${b.error ?? r.status}`);
      }
    } finally { setBusy(false); }
  }

  function parseTeamLabels(): string[] {
    const fromTextarea = teamLabels
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (fromTextarea.length > 0) return fromTextarea.slice(0, 20);
    const single = label.trim();
    return [single || "presenter"];
  }

  async function issueOneLink(usedLabel: string): Promise<IssuedLink | null> {
    const r = await postAdmin("/api/admin/sales-presenter/link", {
      label: usedLabel.slice(0, 80),
      accessHours,
      ttlHours: accessHours,
    });
    if (!r.ok) {
      const b = await r.json().catch(() => ({} as { error?: string }));
      throw new Error(`Link failed for "${usedLabel}": ${b.error ?? r.status}`);
    }
    const data = (await r.json()) as { url: string; expiresAt: string; accessHours?: number };
    const fullUrl = data.url.startsWith("http")
      ? data.url
      : `${window.location.origin}${data.url}`;
    return {
      url: fullUrl,
      expiresAt: data.expiresAt,
      label: usedLabel,
      accessHours: data.accessHours ?? accessHours,
      copied: false,
    };
  }

  async function issueLink() {
    setBusy(true); setMsg(null);
    try {
      const labels = parseTeamLabels();
      const issued: IssuedLink[] = [];
      for (const usedLabel of labels) {
        const row = await issueOneLink(usedLabel);
        if (row) issued.push(row);
      }
      if (issued.length === 1) {
        const copied = await copyToClipboardWithFallback(issued[0]!.url);
        issued[0]!.copied = copied;
      }
      setLinks((prev) => [...issued, ...prev].slice(0, 40));
      setMsg(
        issued.length === 1
          ? null
          : `Issued ${issued.length} single-use presenter links (${accessHours}h access each). Copy each URL below.`,
      );
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Network error");
    } finally { setBusy(false); }
  }

  async function copyLink(target: IssuedLink) {
    const ok = await copyToClipboardWithFallback(target.url);
    setLinks((prev) =>
      prev.map((row) => (row.url === target.url && row.label === target.label ? { ...row, copied: ok } : row)),
    );
  }

  async function copyAllLinks() {
    if (links.length === 0) return;
    const text = links.map((l) => `${l.label}\t${l.url}\t${l.accessHours}h`).join("\n");
    const ok = await copyToClipboardWithFallback(text);
    setMsg(ok ? `Copied ${links.length} link(s) to clipboard.` : "Couldn't auto-copy — copy each URL manually.");
  }

  const launchHref = "/demo?presenter=1";
  const accessLabel =
    ACCESS_DURATION_OPTIONS.find((o) => o.hours === accessHours)?.label ?? `${accessHours} hours`;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-4" data-testid="sales-presenter-panel">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Sales presenter mode</h2>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${status?.active ? "bg-emerald-900/40 text-emerald-300" : "bg-gray-800 text-gray-500"}`}
          data-testid="sales-presenter-status"
        >
          {status?.active ? `Active${status.label ? ` · ${status.label}` : ""}` : "Inactive on this browser"}
        </span>
      </div>

      <p className="text-xs text-gray-400">
        Skip the demo email gate, hide the 15-minute countdown, and suppress the PDF watermark for live customer demos. Every issuance is audited.
      </p>

      {/* Shared label + access duration */}
      <div className="grid gap-3 sm:grid-cols-2 max-w-xl">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1" htmlFor="sp-label">
            Session label (audit log)
          </label>
          <input
            id="sp-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. acme-corp-may-13"
            className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5 w-full"
            data-testid="sales-presenter-label"
            maxLength={80}
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1" htmlFor="sp-access-hours">
            Access duration
          </label>
          <select
            id="sp-access-hours"
            value={accessHours}
            onChange={(e) => setAccessHours(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5 w-full"
            data-testid="sales-presenter-access-hours"
          >
            {ACCESS_DURATION_OPTIONS.map((opt) => (
              <option key={opt.hours} value={opt.hours}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-gray-500">
            How long each salesperson can use the demo after they open their link (or you issue a cookie). Max 7 days.
          </p>
        </div>
      </div>

      {/* Path 1: Cookie on THIS browser */}
      <div className="rounded-lg border border-emerald-900/40 bg-emerald-950/20 p-3" data-testid="sales-presenter-cookie-card">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-sm font-semibold text-emerald-200">1. Present from this browser</h3>
          <span className="text-[10px] uppercase tracking-wider text-emerald-400/70">cookie · {accessLabel}</span>
        </div>
        <p className="text-xs text-emerald-100/70 mb-3">
          Use this if <strong>you</strong> are running the demo on this device. Sets a signed cookie on this browser only.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={issue}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-medium px-3 py-1.5 rounded-md"
            data-testid="sales-presenter-issue"
          >
            Issue presenter cookie
          </button>
          <button
            onClick={stop}
            disabled={busy || !status?.active}
            className="bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-200 text-sm font-medium px-3 py-1.5 rounded-md"
            data-testid="sales-presenter-stop"
          >
            Stop
          </button>
          {status?.active && (
            <a
              href={launchHref}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1"
              data-testid="sales-presenter-launch-button"
            >
              Open demo ↗
            </a>
          )}
          {/* Hidden legacy link for back-compat with prior test selectors. */}
          {status?.active && (
            <a
              href={launchHref}
              target="_blank"
              rel="noopener noreferrer"
              className="sr-only"
              data-testid="sales-presenter-launch"
            >
              Open /demo?presenter=1
            </a>
          )}
        </div>
        {status?.active && status.expiresAt && (
          <p className="mt-2 text-[11px] text-emerald-300/80">
            Cookie expires {new Date(status.expiresAt).toLocaleString()}.
          </p>
        )}
      </div>

      {/* Path 2: Single-use link(s) for teammates */}
      <div className="rounded-lg border border-indigo-900/40 bg-indigo-950/20 p-3" data-testid="sales-presenter-link-card">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-sm font-semibold text-indigo-200">2. Issue demo links to teammates</h3>
          <span className="text-[10px] uppercase tracking-wider text-indigo-400/70">single-use · {accessLabel} access</span>
        </div>
        <p className="text-xs text-indigo-100/70 mb-3">
          Mint one or more single-use presenter URLs. Each teammate opens their link once — they get <strong>{accessLabel}</strong> of demo access on their browser. Paste names/emails below (one per line), or leave blank to use the session label above.
        </p>
        <label className="block text-[11px] uppercase tracking-wider text-indigo-300/70 mb-1" htmlFor="sp-team-labels">
          Teammates (optional — one label per line, max 20)
        </label>
        <textarea
          id="sp-team-labels"
          value={teamLabels}
          onChange={(e) => setTeamLabels(e.target.value)}
          placeholder={"jordan@yourteam.com\nmorgan-sales\nalex-rep"}
          rows={3}
          className="bg-gray-950 border border-indigo-900/60 text-indigo-100 text-xs font-mono rounded-md px-2 py-1.5 w-full mb-3"
          data-testid="sales-presenter-team-labels"
        />
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={issueLink}
            disabled={busy}
            className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-3 py-1.5 rounded-md"
            data-testid="sales-presenter-link"
          >
            {busy ? "Issuing…" : "Issue presenter link(s)"}
          </button>
          {links.length > 0 && (
            <>
              <button
                onClick={() => void copyAllLinks()}
                className="bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-medium px-2.5 py-1.5 rounded-md"
                data-testid="sales-presenter-link-copy-all"
              >
                Copy all
              </button>
              <button
                onClick={() => { setLinks([]); }}
                className="text-xs text-indigo-300/70 hover:text-indigo-200 underline underline-offset-4"
                data-testid="sales-presenter-link-clear"
              >
                Clear
              </button>
            </>
          )}
        </div>
        {links.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="sales-presenter-links-list">
            {links.map((link) => (
              <div key={`${link.label}-${link.url}`} className="space-y-1">
                <div className="text-[11px] text-indigo-300/90 font-medium">
                  {link.label} · {link.accessHours}h access
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <input
                    type="text"
                    value={link.url}
                    readOnly
                    onFocus={(e) => e.currentTarget.select()}
                    className="bg-gray-950 border border-indigo-900/60 text-indigo-100 text-xs font-mono rounded-md px-2 py-1.5 flex-1 min-w-[260px]"
                    data-testid="sales-presenter-link-url"
                  />
                  <button
                    onClick={() => void copyLink(link)}
                    className="bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-medium px-2.5 py-1.5 rounded-md"
                    data-testid="sales-presenter-link-copy"
                  >
                    {link.copied ? "Copied" : "Copy"}
                  </button>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-800 hover:bg-gray-700 text-gray-100 text-xs font-medium px-2.5 py-1.5 rounded-md"
                    data-testid="sales-presenter-link-open"
                  >
                    Open ↗
                  </a>
                </div>
                <p className="text-[11px] text-indigo-300/70">
                  Single use · link + access expire {new Date(link.expiresAt).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {msg && (
        <p className="text-xs text-gray-400" data-testid="sales-presenter-msg">{msg}</p>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="text-[11px] uppercase tracking-wider text-gray-500 hover:text-gray-300"
          data-testid="sales-presenter-help-toggle"
        >
          {showHelp ? "− Hide" : "+ How presenter mode works"}
        </button>
        {showHelp && (
          <div className="mt-2 text-[11px] text-gray-400 leading-relaxed space-y-1.5" data-testid="sales-presenter-help">
            <p>
              <strong className="text-gray-300">Cookie path</strong> sets a signed cookie on the current browser for the access duration you pick (2h–7d). Use it when you're presenting yourself. The cookie is HttpOnly so it can't be exfiltrated via JS.
            </p>
            <p>
              <strong className="text-gray-300">Link path</strong> mints a one-time URL. The first browser to open it gets a presenter cookie lasting that same access duration; opening it a second time returns 410 Gone. Send the URL to a teammate via your usual chat tool.
            </p>
            <p>
              Both paths are recorded in the owner audit log under the session label you provide. Either path bypasses the demo email gate, hides the 15-minute countdown, suppresses the PDF watermark, and unlocks <code>POST /api/demo/presenter/reset</code> so you can re-seed demo data mid-call.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const ONBOARDING_TRACKS = [
  { id: "sales-rep", label: "Sales Rep" },
  { id: "chief-of-staff", label: "Chief of Staff" },
  { id: "ops", label: "Ops" },
] as const;

const LMS_SUPPORT_ROLE_OPTIONS = [
  { id: "lms-admin", label: "LMS Admin (learner support)" },
  { id: "cos", label: "Chief of Staff (LMS)" },
  { id: "ops-admin", label: "Ops Admin (LMS)" },
] as const;

/**
 * Owner-only: mint signed unlock for hire curriculum + assign LMS support admins.
 * LMS support admins cannot unlock demos — they help learners in the LMS console.
 */
function OnboardingReviewPanel() {
  const [track, setTrack] = useState<(typeof ONBOARDING_TRACKS)[number]["id"]>(
    "sales-rep",
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [staffEmail, setStaffEmail] = useState("");
  const [staffRole, setStaffRole] = useState<(typeof LMS_SUPPORT_ROLE_OPTIONS)[number]["id"]>(
    "lms-admin",
  );
  const configured = (
    import.meta.env.VITE_ONBOARDING_APP_URL as string | undefined
  )?.trim();
  const base = (configured || "http://localhost:5173").replace(/\/$/, "");

  async function launchReview() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminFetch("/api/admin/onboarding-review/launch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ track }),
      });
      const data = (await r.json().catch(() => ({}))) as {
        url?: string;
        error?: string;
        detail?: string;
      };
      if (!r.ok || !data.url) {
        setMsg(data.detail || data.error || `Launch failed (${r.status})`);
        return;
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
      setMsg("Opened unlocked curriculum in the training app.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Network error");
    } finally {
      setBusy(false);
    }
  }

  async function assignStaff() {
    const email = staffEmail.trim();
    if (!email) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminFetch("/api/admin/lms-staff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role: staffRole }),
      });
      const data = (await r.json().catch(() => ({}))) as {
        mode?: string;
        error?: string;
        detail?: string;
      };
      if (!r.ok) {
        setMsg(data.detail || data.error || `Assign failed (${r.status})`);
        return;
      }
      setMsg(
        data.mode === "invited"
          ? `Invited ${email} as ${staffRole}.`
          : `Updated ${email} → ${staffRole}.`,
      );
      setStaffEmail("");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Network error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-4"
      data-testid="onboarding-review-panel"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-white tracking-tight">
          Hire training LMS
        </h2>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-indigo-950 text-indigo-300 shrink-0">
          Owner only
        </span>
      </div>

      <p className="text-xs text-gray-400">
        Open the hire curriculum for your own walkthrough, or assign someone to
        support learners in the training app.
      </p>

      <div className="border border-indigo-900/60 bg-indigo-950/20 rounded-lg p-3 space-y-3">
        <h3 className="text-xs font-semibold text-indigo-200 uppercase tracking-wide">
          Open unlocked curriculum
        </h3>
        <div>
          <label
            className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1"
            htmlFor="onboarding-review-track"
          >
            Track to preview
          </label>
          <select
            id="onboarding-review-track"
            value={track}
            onChange={(e) =>
              setTrack(e.target.value as (typeof ONBOARDING_TRACKS)[number]["id"])
            }
            className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5 w-full max-w-sm"
            data-testid="onboarding-review-track"
          >
            {ONBOARDING_TRACKS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void launchReview()}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-md transition-colors"
            data-testid="onboarding-review-launch"
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Open unlocked curriculum
          </button>
          <a
            href={`${base}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm text-gray-300 hover:text-white px-3 py-1.5 rounded-md border border-gray-700 hover:border-gray-500 transition-colors"
          >
            Open training app
          </a>
        </div>
      </div>

      <div className="border border-gray-800 rounded-lg p-3 space-y-3">
        <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">
          Assign training support
        </h3>
        <p className="text-[11px] text-gray-500">
          Gives them admin tools in the training app (cohorts, reviews, Day 10)
          — not full curriculum unlock.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label
              className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1"
              htmlFor="lms-staff-email"
            >
              Email
            </label>
            <input
              id="lms-staff-email"
              type="email"
              value={staffEmail}
              onChange={(e) => setStaffEmail(e.target.value)}
              placeholder="admin@zebiso.com"
              className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5 w-full"
              data-testid="lms-staff-email"
            />
          </div>
          <div>
            <label
              className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1"
              htmlFor="lms-staff-role"
            >
              Role
            </label>
            <select
              id="lms-staff-role"
              value={staffRole}
              onChange={(e) =>
                setStaffRole(
                  e.target.value as (typeof LMS_SUPPORT_ROLE_OPTIONS)[number]["id"],
                )
              }
              className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5"
              data-testid="lms-staff-role"
            >
              {LMS_SUPPORT_ROLE_OPTIONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={busy || !staffEmail.trim()}
            onClick={() => void assignStaff()}
            className="text-sm font-medium bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md border border-gray-700"
            data-testid="lms-staff-assign"
          >
            Assign
          </button>
        </div>
      </div>

      {msg && (
        <p className="text-xs text-gray-300" data-testid="onboarding-review-msg">
          {msg}
        </p>
      )}

      {!configured && (
        <p className="text-[11px] text-amber-400/90">
          Onboarding URL default <code className="text-amber-200">{base}</code>.
          Set <code className="text-amber-200">VITE_ONBOARDING_APP_URL</code> /
          API <code className="text-amber-200">ONBOARDING_APP_URL</code>. Share{" "}
          <code className="text-amber-200">LMS_CROSS_APP_SECRET</code> with the
          onboarding app.
        </p>
      )}
    </div>
  );
}

function DemoLinkCard() {
  const [status, setStatus] = useState<{
    configuredDemoOrgId: number | null;
    demoClerkUserId: string | null;
    healthy: boolean;
    defaultName: string;
    defaultInstitution: string;
    orgs: Array<{ id: number; name: string; institutionName?: string | null; plan: string; isDemo: boolean; reason: string }>;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [institutionName, setInstitutionName] = useState("Northfield University");
  const [orgName, setOrgName] = useState("Northfield Athletics");

  function applyStatus(body: {
    configuredDemoOrgId?: number | null;
    demoClerkUserId?: string | null;
    healthy?: boolean;
    defaultName?: string;
    defaultInstitution?: string;
    orgs?: Array<{ id: number; name: string; institutionName?: string | null; plan: string; isDemo: boolean; reason: string }>;
  }) {
    const defaultName = body.defaultName ?? "Northfield Athletics";
    const defaultInstitution = body.defaultInstitution ?? "Northfield University";
    const orgs = Array.isArray(body.orgs) ? body.orgs : [];
    setStatus({
      configuredDemoOrgId: body.configuredDemoOrgId ?? null,
      demoClerkUserId: body.demoClerkUserId ?? null,
      healthy: Boolean(body.healthy),
      defaultName,
      defaultInstitution,
      orgs,
    });
    const primary = orgs[0];
    if (primary) {
      setOrgName(primary.name || defaultName);
      setInstitutionName(primary.institutionName || primary.name || defaultInstitution);
    } else {
      setOrgName(defaultName);
      setInstitutionName(defaultInstitution);
    }
  }

  async function refresh() {
    try {
      const r = await fetch("/api/admin/demo/sandbox", { credentials: "include" });
      if (!r.ok) return;
      applyStatus(await r.json());
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function repair() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminFetch("/api/admin/demo/repair", { method: "POST" });
      if (!r.ok) {
        setMsg(`Repair failed (${r.status})`);
        return;
      }
      const body = await r.json();
      applyStatus(body);
      setMsg(
        body.healthy
          ? "Demo sandbox repaired — enterprise + is_demo on all presenter targets."
          : "Repair ran but sandbox still unhealthy — check DEMO_ORG_ID / DEMO_CLERK_USER_ID.",
      );
    } catch {
      setMsg("Repair failed (network)");
    } finally {
      setBusy(false);
    }
  }

  async function rename(useDefault: boolean) {
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminFetch("/api/admin/demo/sandbox/rename", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          useDefault
            ? { useDefault: true }
            : { name: orgName.trim(), institutionName: institutionName.trim() },
        ),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({} as { error?: string }));
        setMsg(`Rename failed: ${b.error ?? r.status}`);
        return;
      }
      applyStatus(await r.json());
      setMsg(
        useDefault
          ? "Sandbox branding set to Northfield University (neutral sample)."
          : "Sandbox branding updated.",
      );
    } catch {
      setMsg("Rename failed (network)");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 space-y-3" data-testid="demo-link-card">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-indigo-400" />
        <h3 className="text-sm font-semibold text-white">Demo access</h3>
        <span className="text-xs text-gray-500">— sales channels only</span>
        {status && (
          <span
            className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
              status.healthy
                ? "bg-emerald-900/50 text-emerald-300 border border-emerald-700/50"
                : "bg-amber-900/50 text-amber-200 border border-amber-700/50"
            }`}
            data-testid="demo-sandbox-health"
          >
            {status.healthy ? "Enterprise ready" : "Needs repair"}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">
        Public self-serve <code className="text-gray-300">/demo</code> is off.
        Share a <strong className="text-gray-300 font-medium">sales-presenter</strong>{" "}
        link (below) with teammates, or an owner invitation from Demo Leads.
        Presenter targets must be <code className="text-gray-300">is_demo</code>{" "}
        + <code className="text-gray-300">enterprise</code> or the UI shows Free.
      </p>
      {status && (
        <div className="text-[11px] text-gray-400 space-y-1 font-mono" data-testid="demo-sandbox-status">
          <div>DEMO_ORG_ID={status.configuredDemoOrgId ?? "unset"}</div>
          <div>DEMO_CLERK_USER_ID={status.demoClerkUserId ?? "unset"}</div>
          {status.orgs.length === 0 ? (
            <div className="text-amber-300">No presenter target orgs found.</div>
          ) : (
            status.orgs.map((o) => (
              <div key={o.id}>
                #{o.id} {o.name} · plan={o.plan} · is_demo={String(o.isDemo)} · {o.reason}
              </div>
            ))
          )}
        </div>
      )}
      <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-3 space-y-2" data-testid="demo-sandbox-branding">
        <div className="text-[11px] uppercase tracking-wider text-gray-500">Sandbox branding (shown in the demo shell)</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            type="text"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Northfield Athletics"
            className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5"
            data-testid="demo-sandbox-name"
            maxLength={120}
          />
          <input
            type="text"
            value={institutionName}
            onChange={(e) => setInstitutionName(e.target.value)}
            placeholder="Northfield University"
            className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-3 py-1.5"
            data-testid="demo-sandbox-institution"
            maxLength={200}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void rename(false)}
            disabled={busy || !orgName.trim() || !institutionName.trim()}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 border border-gray-700 text-gray-200 hover:text-white disabled:opacity-50"
            data-testid="demo-sandbox-rename"
          >
            Save branding
          </button>
          <button
            type="button"
            onClick={() => void rename(true)}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-900/50 border border-indigo-700/50 text-indigo-200 hover:text-white disabled:opacity-50"
            data-testid="demo-sandbox-rename-default"
          >
            Use Northfield University
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void repair()}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
          data-testid="demo-sandbox-repair"
        >
          {busy ? "Working…" : "Repair demo → Enterprise"}
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 border border-gray-700 text-gray-300 hover:text-white"
          data-testid="demo-sandbox-refresh"
        >
          Refresh status
        </button>
      </div>
      {msg && <p className="text-xs text-gray-300" data-testid="demo-sandbox-msg">{msg}</p>}
    </div>
  );
}

// ─── Live business panel (Task #435) ──────────────────────────────────────
// Five owner-only insight cards backed by /api/admin/insights/*. Hooks are
// the generated React Query hooks (typed against the OpenAPI spec) and poll
// every 60s. Each card shows a "Updated Xs ago" footer and degrades to "—"
// when the endpoint hasn't responded yet.

import {
  useAdminGetInsightsRevenue,
  useAdminGetInsightsTrials,
  useAdminGetInsightsAtRisk,
  useAdminGetInsightsLifecycle,
  useAdminGetInsightsDeliverability,
  getAdminGetInsightsRevenueQueryKey,
  getAdminGetInsightsTrialsQueryKey,
  getAdminGetInsightsAtRiskQueryKey,
  getAdminGetInsightsLifecycleQueryKey,
  getAdminGetInsightsDeliverabilityQueryKey,
} from "@workspace/api-client-react";

function useFreshness(generatedAt?: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);
  if (!generatedAt) return "—";
  const secs = Math.max(0, Math.round((now - new Date(generatedAt).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s ago`;
}

function InsightCard({
  title,
  icon: Icon,
  generatedAt,
  children,
  iconClass,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  generatedAt?: string;
  children: React.ReactNode;
  iconClass?: string;
}) {
  const fresh = useFreshness(generatedAt);
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${iconClass ?? "text-indigo-400"}`} />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      <div className="flex-1">{children}</div>
      <div className="mt-3 text-[11px] text-gray-500">Updated {fresh}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: number | string; tone?: "danger" | "warn" | "ok" }) {
  const color = tone === "danger" ? "text-red-400" : tone === "warn" ? "text-yellow-400" : tone === "ok" ? "text-green-400" : "text-white";
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-gray-400">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

function fmtUsd(cents: number | undefined): string {
  if (cents === undefined || cents === null) return "—";
  if (cents >= 1000) return `$${(cents / 1000).toFixed(1)}k`;
  return `$${cents}`;
}

function healthDot(h: "green" | "yellow" | "red" | undefined) {
  const c = h === "red" ? "bg-red-500" : h === "yellow" ? "bg-yellow-500" : h === "green" ? "bg-green-500" : "bg-gray-600";
  return <span className={`inline-block h-2 w-2 rounded-full ${c}`} />;
}

function LiveBusinessPanel() {
  // Pass the generated queryKey helpers so we get fully-typed UseQueryOptions
  // (queryKey is required on the generated hook signature) without resorting
  // to type escapes.
  const POLL = { refetchInterval: 60_000, refetchOnWindowFocus: false } as const;
  const revenueQ = useAdminGetInsightsRevenue({
    query: { queryKey: getAdminGetInsightsRevenueQueryKey(), ...POLL },
  });
  const trialsQ = useAdminGetInsightsTrials({
    query: { queryKey: getAdminGetInsightsTrialsQueryKey(), ...POLL },
  });
  const atRiskQ = useAdminGetInsightsAtRisk({
    query: { queryKey: getAdminGetInsightsAtRiskQueryKey(), ...POLL },
  });
  const lifecycleQ = useAdminGetInsightsLifecycle({
    query: { queryKey: getAdminGetInsightsLifecycleQueryKey(), ...POLL },
  });
  const delivQ = useAdminGetInsightsDeliverability({
    query: { queryKey: getAdminGetInsightsDeliverabilityQueryKey(), ...POLL },
  });

  const revenue = revenueQ.data;
  const trials = trialsQ.data;
  const atRisk = atRiskQ.data;
  const lifecycle = lifecycleQ.data;
  const deliv = delivQ.data;

  const top3Trials = (trials?.trialsEndingSoon ?? []).slice(0, 3);
  const top3Risks = (atRisk?.tenants ?? []).slice(0, 3);
  const top3Stuck = (lifecycle?.stuckNegotiations.rounds ?? []).slice(0, 3);
  const lifecycleEntries = Object.entries(lifecycle?.contractsByLifecycleState ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  const deltaTone = (revenue?.mrrDelta ?? 0) >= 0 ? "ok" : "danger";
  const deltaStr = revenue
    ? `${revenue.mrrDelta >= 0 ? "+" : ""}${fmtUsd(Math.abs(revenue.mrrDelta))} (${revenue.mrrDeltaPct}%)`
    : "—";

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Activity className="h-4 w-4 text-indigo-400" />
        <h2 className="text-sm font-semibold text-white">Live business</h2>
        <span className="text-xs text-gray-500">— polled every 60s</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="live-business-panel">
        <InsightCard title="Revenue" icon={Heart} iconClass="text-rose-400" generatedAt={revenue?.generatedAt}>
          <div className="space-y-1.5">
            <MiniStat label="MRR" value={fmtUsd(revenue?.mrr)} tone="ok" />
            <MiniStat label="ARR" value={fmtUsd(revenue?.arr)} />
            <MiniStat label="MRR Δ 30d" value={deltaStr} tone={revenue ? deltaTone : undefined} />
            <MiniStat label="Past due" value={revenue?.pastDue ?? "—"} tone={(revenue?.pastDue ?? 0) > 0 ? "danger" : undefined} />
            <MiniStat label="Failed pays 24h" value={revenue?.failedPayments24h ?? "—"} tone={(revenue?.failedPayments24h ?? 0) > 0 ? "danger" : undefined} />
          </div>
        </InsightCard>

        <InsightCard title="Trials" icon={Repeat} iconClass="text-sky-400" generatedAt={trials?.generatedAt}>
          <div className="space-y-1.5">
            <MiniStat label="Pending → Verified" value={`${trials?.funnel30d.pending ?? 0} → ${trials?.funnel30d.verified ?? 0}`} />
            <MiniStat label="Signed up 30d" value={trials?.funnel30d.signedUp ?? "—"} tone={(trials?.funnel30d.signedUp ?? 0) > 0 ? "ok" : undefined} />
            <MiniStat label="Verify rate" value={`${trials?.funnel30d.verifyRate ?? 0}%`} />
            <MiniStat label="Trialing now" value={trials?.trialing ?? "—"} />
            <MiniStat label="Ending in 7d" value={trials?.trialsEndingSoon.length ?? "—"} tone={(trials?.trialsEndingSoon.length ?? 0) > 0 ? "warn" : undefined} />
          </div>
          {top3Trials.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-800 space-y-1 text-[11px]">
              {top3Trials.map((t) => (
                <Link key={t.orgId} href={`/admin/orgs/${t.orgId}`} className="block text-gray-300 hover:text-white truncate">
                  · {t.name}
                  <span className="text-gray-500"> · {t.lastActivityAt ? "active" : "no activity"}</span>
                </Link>
              ))}
            </div>
          )}
        </InsightCard>

        <InsightCard title="At-risk tenants" icon={AlertTriangle} iconClass="text-amber-400" generatedAt={atRisk?.generatedAt}>
          <div className="space-y-1.5">
            <MiniStat label="Total" value={atRisk?.total ?? "—"} tone={(atRisk?.total ?? 0) > 0 ? "danger" : undefined} />
            <div className="text-[11px] text-gray-500">
              Stale 14d AND seat usage &gt; 80%, sorted by score.
            </div>
          </div>
          {top3Risks.length > 0 ? (
            <div className="mt-2 pt-2 border-t border-gray-800 space-y-1 text-[11px]">
              {top3Risks.map((t) => (
                <Link key={t.orgId} href={`/admin/orgs/${t.orgId}`} className="block text-gray-300 hover:text-white truncate">
                  · {t.name}
                  <span className="text-gray-500"> · {t.memberCount}/{t.seatCap} seats · {t.daysSinceActivity ?? "—"}d idle</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="mt-2 pt-2 border-t border-gray-800 text-[11px] text-gray-500">No at-risk tenants.</div>
          )}
        </InsightCard>

        <InsightCard title="Cross-tenant lifecycle" icon={BarChart3} iconClass="text-violet-400" generatedAt={lifecycle?.generatedAt}>
          <div className="space-y-1.5">
            <MiniStat label="Expiring 30d" value={lifecycle?.expiring.next30d ?? "—"} tone={(lifecycle?.expiring.next30d ?? 0) > 0 ? "warn" : undefined} />
            <MiniStat label="Expiring 60d" value={lifecycle?.expiring.next60d ?? "—"} />
            <MiniStat label="Overdue active" value={lifecycle?.expiring.overdueActive ?? "—"} tone={(lifecycle?.expiring.overdueActive ?? 0) > 0 ? "danger" : undefined} />
            <MiniStat label="Stuck negotiations" value={lifecycle?.stuckNegotiations.count ?? "—"} tone={(lifecycle?.stuckNegotiations.count ?? 0) > 0 ? "warn" : undefined} />
          </div>
          {top3Stuck.length > 0 ? (
            <div className="mt-2 pt-2 border-t border-gray-800 space-y-0.5 text-[11px] text-gray-400">
              {top3Stuck.map((r) => (
                <div key={r.id} className="flex justify-between">
                  <span className="truncate">#{r.contractId} · {r.recipient ?? "—"}</span>
                  <span className="tabular-nums text-gray-300">{r.daysOpen}d</span>
                </div>
              ))}
            </div>
          ) : lifecycleEntries.length > 0 ? (
            <div className="mt-2 pt-2 border-t border-gray-800 space-y-0.5 text-[11px] text-gray-400">
              {lifecycleEntries.map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="tabular-nums text-gray-300">{v}</span>
                </div>
              ))}
            </div>
          ) : null}
          {lifecycle?.expiring.drilldownHref && (
            <div className="mt-2 pt-2 border-t border-gray-800 text-[11px]">
              <Link href={lifecycle.expiring.drilldownHref} className="text-indigo-400 hover:text-indigo-300">
                View expiring contracts →
              </Link>
            </div>
          )}
        </InsightCard>

        <InsightCard title="Deliverability" icon={Mail} iconClass="text-emerald-400" generatedAt={deliv?.generatedAt}>
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-gray-400 flex items-center gap-1.5">
                {healthDot(deliv?.resend24h.health)} Resend bounce 24h
              </span>
              <span className={`text-sm font-semibold tabular-nums ${deliv?.resend24h.health === "red" ? "text-red-400" : deliv?.resend24h.health === "yellow" ? "text-yellow-400" : "text-white"}`}>
                {deliv ? `${deliv.resend24h.bouncePct}%` : "—"}
              </span>
            </div>
            <MiniStat label="Resend delivered" value={deliv?.resend24h.delivered ?? "—"} />
            <MiniStat label="Resend bounced" value={deliv?.resend24h.bounced ?? "—"} tone={(deliv?.resend24h.bounced ?? 0) > 0 ? "warn" : undefined} />
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-gray-400 flex items-center gap-1.5">
                {healthDot(deliv?.stripe24h.health)} Stripe errors 24h
              </span>
              <span className={`text-sm font-semibold tabular-nums ${(deliv?.stripe24h.errors ?? 0) > 0 ? "text-red-400" : "text-white"}`}>
                {deliv?.stripe24h.errors ?? "—"}
              </span>
            </div>
          </div>
          {deliv?.healthHref && (
            <div className="mt-2 pt-2 border-t border-gray-800 text-[11px]">
              <Link href={deliv.healthHref} className="text-indigo-400 hover:text-indigo-300">
                View raw webhook health →
              </Link>
            </div>
          )}
        </InsightCard>
      </div>
    </div>
  );
}

function Sparkline({ data, width = 80, height = 24 }: { data: number[]; width?: number; height?: number }) {
  const series = !data || data.length === 0 ? Array.from({ length: 14 }, () => 0) : data;
  const max = Math.max(...series, 1);
  const total = series.reduce((s, v) => s + v, 0);
  const n = series.length;
  const stepX = n > 1 ? width / (n - 1) : 0;
  const points = series.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  const stroke = total === 0 ? "#4b5563" : "#3b82f6";
  const fill = total === 0 ? "rgba(75,85,99,0.15)" : "rgba(59,130,246,0.15)";
  return (
    <div className="flex items-center gap-2" title={`${total} events in last 14 days`}>
      <svg width={width} height={height} className="block">
        <polygon points={areaPoints} fill={fill} />
        <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <span className="text-xs text-gray-500 tabular-nums">{total}</span>
    </div>
  );
}

interface PersonaOverview {
  totalOrgs: number;
  totalMembers: number;
  membersWithPersonas: number;
  activeMembers30d: number;
  avgPersonasPerAssignedMember: number;
  avgPersonasPerActiveMember30d: number;
  windowDays: number;
  personas: {
    persona: string;
    assignedMembers: number;
    orgsUsing: number;
    orgAdoptionPct: number;
    activeMembers30d: number;
    dormantMembers: number;
    events30d: number;
  }[];
  topOrgsByDiversity: {
    orgId: number;
    name: string;
    distinctPersonas: number;
    memberCount: number;
  }[];
}

function PersonasPanel() {
  const [data, setData] = useState<PersonaOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/admin/personas", { credentials: "include" });
        if (!res.ok) {
          if (!cancelled) setError(`Failed to load personas (${res.status})`);
          return;
        }
        const json = (await res.json()) as PersonaOverview;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Network error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-sm text-gray-500" data-testid="personas-loading">
        Loading persona analytics…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-gray-900 border border-red-800 rounded-xl p-6 text-sm text-red-400">
        {error ?? "No persona data available."}
      </div>
    );
  }

  const maxAssigned = Math.max(1, ...data.personas.map((p) => p.assignedMembers));

  return (
    <div className="space-y-4" data-testid="personas-panel">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Orgs</p>
          <span className="text-2xl font-bold text-white">{fmt(data.totalOrgs)}</span>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Members with persona</p>
          <span className="text-2xl font-bold text-white">{fmt(data.membersWithPersonas)}</span>
          <div className="text-xs text-gray-500 mt-0.5">of {fmt(data.totalMembers)} total</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Active members (30d)</p>
          <span className="text-2xl font-bold text-white">{fmt(data.activeMembers30d)}</span>
          <div className="text-xs text-gray-500 mt-0.5">fired persona-attributed events</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Avg personas / active member</p>
          <span className="text-2xl font-bold text-white">{data.avgPersonasPerActiveMember30d}</span>
          <div className="text-xs text-gray-500 mt-0.5">assigned: {data.avgPersonasPerAssignedMember}</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Activity window</p>
          <span className="text-2xl font-bold text-white">{data.windowDays}d</span>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
          <UserCircle2 className="h-4 w-4 text-indigo-400" />
          <h2 className="text-sm font-semibold text-white">Persona adoption & activity</h2>
          <span className="text-xs text-gray-500 ml-auto">% of orgs assigning each persona; activity in last {data.windowDays}d</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="personas-table">
            <thead>
              <tr className="border-b border-gray-800">
                {["Persona", "Orgs using", "Adoption %", "Members assigned", "Active 30d", "Dormant", "Events 30d"].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.personas.map((p) => {
                const barPct = Math.round((p.assignedMembers / maxAssigned) * 100);
                return (
                  <tr key={p.persona} className="border-b border-gray-800/50" data-testid={`personas-row-${p.persona}`}>
                    <td className="px-4 py-2.5 text-gray-200 font-medium">{p.persona}</td>
                    <td className="px-4 py-2.5 text-gray-300 tabular-nums">{p.orgsUsing}</td>
                    <td className="px-4 py-2.5 text-gray-300 tabular-nums">{p.orgAdoptionPct}%</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 bg-gray-800 rounded-full flex-1 max-w-[200px] overflow-hidden">
                          <div className="h-full bg-indigo-500" style={{ width: `${barPct}%` }} />
                        </div>
                        <span className="text-xs text-gray-300 tabular-nums w-10">{p.assignedMembers}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-green-400 tabular-nums">{p.activeMembers30d}</td>
                    <td className={`px-4 py-2.5 tabular-nums ${p.dormantMembers > 0 ? "text-yellow-400" : "text-gray-500"}`}>{p.dormantMembers}</td>
                    <td className="px-4 py-2.5 text-gray-300 tabular-nums">{p.events30d}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-800">
          <BarChart3 className="h-4 w-4 text-violet-400" />
          <h2 className="text-sm font-semibold text-white">Top orgs by persona diversity</h2>
        </div>
        {data.topOrgsByDiversity.length === 0 ? (
          <p className="px-5 py-4 text-sm text-gray-500">No orgs have personas assigned yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                {["Org", "Distinct personas", "Members with persona"].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.topOrgsByDiversity.map((o) => (
                <tr key={o.orgId} className="border-b border-gray-800/50">
                  <td className="px-4 py-2.5">
                    <Link href={`/admin/orgs/${o.orgId}`} className="text-blue-400 hover:underline">{o.name}</Link>
                  </td>
                  <td className="px-4 py-2.5 text-gray-300 tabular-nums">{o.distinctPersonas}</td>
                  <td className="px-4 py-2.5 text-gray-300 tabular-nums">{o.memberCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [, setLocation] = useLocation();
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [activity, setActivity] = useState<Record<string, number[]>>({});
  const [revenue, setRevenue] = useState<RevenueData | null>(null);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingRevenue, setLoadingRevenue] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [activeTab, setActiveTab] = useState<"orgs" | "revenue" | "personas">("orgs");

  const checkAuth = useCallback(async () => {
    const res = await fetch("/api/admin/me", { credentials: "include" });
    const data = await res.json() as { authenticated: boolean; csrfToken?: string };
    // /me slides the session cookie — keep the SPA CSRF cache in sync so
    // mutating calls (issue presenter cookie, etc.) don't send a stale token.
    if (data.csrfToken) setAdminCsrfToken(data.csrfToken);
    if (!data.authenticated) {
      setAdminCsrfToken(null);
      setLocation("/admin/login");
    }
  }, [setLocation]);

  const fetchOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const res = await fetch("/api/admin/orgs", { credentials: "include" });
      if (res.status === 401) { setLocation("/admin/login"); return; }
      const data = await res.json() as OrgRow[];
      setOrgs(data);
    } catch { /* ignore */ }
    finally { setLoadingOrgs(false); }
  }, [setLocation]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/orgs/activity", { credentials: "include" });
      if (res.status === 401) { setLocation("/admin/login"); return; }
      const data = await res.json() as { days: string[]; activity: Record<string, number[]> };
      setActivity(data.activity ?? {});
    } catch { /* ignore */ }
  }, [setLocation]);

  const fetchRevenue = useCallback(async () => {
    setLoadingRevenue(true);
    try {
      const res = await fetch("/api/admin/revenue", { credentials: "include" });
      if (res.status === 401) { setLocation("/admin/login"); return; }
      const data = await res.json() as RevenueData;
      setRevenue(data);
    } catch { /* ignore */ }
    finally { setLoadingRevenue(false); }
  }, [setLocation]);

  useEffect(() => {
    checkAuth();
    fetchOrgs();
    fetchActivity();
    fetchRevenue();
  }, [checkAuth, fetchOrgs, fetchActivity, fetchRevenue]);

  async function handleLogout() {
    await fetch("/api/admin/logout", { method: "POST", credentials: "include", headers: adminCsrfHeaders() });
    setLocation("/admin/login");
  }

  async function handleExportCSV() {
    const link = document.createElement("a");
    link.href = "/api/admin/orgs/export/csv";
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const filtered = orgs.filter((o) => {
    if (search) {
      const q = search.toLowerCase();
      if (!o.name.toLowerCase().includes(q) && !(o.division ?? "").toLowerCase().includes(q) && !(o.conference ?? "").toLowerCase().includes(q)) return false;
    }
    if (planFilter !== "all" && o.plan !== planFilter) return false;
    if (statusFilter !== "all") {
      if (statusFilter === "active" && !o.stripeSubscriptionId) return false;
      if (statusFilter === "trial" && (!o.stripeCustomerId || o.stripeSubscriptionId)) return false;
      if (statusFilter === "free" && (o.stripeCustomerId || o.stripeSubscriptionId)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number = a[sortKey] ?? "";
    let bv: string | number = b[sortKey] ?? "";
    if (sortKey === "memberCount" || sortKey === "coachCount" || sortKey === "contractCount") {
      av = Number(av); bv = Number(bv);
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  function SortIcon({ k }: { k: SortKey }) {
    if (sortKey !== k) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="h-3 w-3 text-blue-400" /> : <ChevronDown className="h-3 w-3 text-blue-400" />;
  }

  const pieData = revenue
    ? Object.entries(revenue.planCounts)
        .filter(([, v]) => (v ?? 0) > 0)
        .map(([k, v]) => ({ name: getPlanBadgeStyle(k).label, planKey: k, value: v ?? 0 }))
    : [];

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <img src="/logo.svg" alt="Zindeal" className="w-7 h-7 rounded-full" />
          <span className="font-bold text-sm">Zindeal</span>
          <span className="text-gray-600 text-sm mx-1">/</span>
          <span className="text-gray-400 text-sm">Owner Console</span>
          <div className="ml-auto flex items-center gap-2">
            <Link
              href="/admin/demo-leads"
              className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
              data-testid="admin-nav-demo-leads"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Demo Leads
            </Link>
            <Link
              href="/admin/health"
              className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
              data-testid="admin-nav-health"
            >
              Health
            </Link>
            <button
              onClick={() => { fetchOrgs(); fetchActivity(); fetchRevenue(); }}
              className="p-1.5 text-gray-400 hover:text-white rounded-md hover:bg-gray-800 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-gray-800 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <DemoLinkCard />
        <LiveBusinessPanel />
        {revenue && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: "MRR", value: fmtCurrency(revenue.mrr), icon: DollarSign, sub: `ARR ${fmtCurrency(revenue.arr)}` },
              { label: "Paying Customers", value: fmt(revenue.payingCustomers), icon: Users, sub: `${revenue.trialCustomers} on trial` },
              { label: "Churn Rate", value: `${revenue.churnRate}%`, icon: Percent, sub: `${revenue.cancellationsThisMonth} cancelled this mo.` },
              { label: "Trial → Paid", value: `${revenue.trialConversionRate}%`, icon: TrendingUp, sub: `ARPA ${fmtCurrency(revenue.arpa)}/mo` },
              {
                label: "Demo Conversions",
                value: `${revenue.demoConversionRate ?? 0}%`,
                icon: Sparkles,
                sub: `${fmt(revenue.demoConversions30d ?? 0)} of ${fmt(revenue.demoClicks30d ?? 0)} clicks (30d) · ${fmt(revenue.demoConversionsTotal ?? 0)} all-time`,
              },
            ].map((card) => (
              <div key={card.label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">{card.label}</span>
                  <card.icon className="h-4 w-4 text-gray-600" />
                </div>
                <div className="text-xl font-bold text-white">{card.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{card.sub}</div>
              </div>
            ))}
          </div>
        )}

        <OnboardingReviewPanel />
        <DemoFunnelPanel />
        <DemoConversionPanel />
        <SalesPresenterPanel />

        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
          {(["orgs", "revenue", "personas"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? "bg-gray-800 text-white" : "text-gray-500 hover:text-gray-300"}`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab === "orgs" ? "Organizations" : tab === "revenue" ? "Revenue & Health" : "Personas"}
            </button>
          ))}
        </div>

        {activeTab === "personas" && <PersonasPanel />}

        {activeTab === "orgs" && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-800 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search organizations…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="all">All Plans</option>
                  {PLAN_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="trial">Trial</option>
                  <option value="free">Free</option>
                </select>
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white px-3 py-2 rounded-lg text-sm transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  CSV
                </button>
              </div>
            </div>

            <div className="text-xs text-gray-500 px-4 py-2 border-b border-gray-800">
              Showing {sorted.length} of {orgs.length} organizations
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    {[
                      { key: "name" as SortKey, label: "Organization" },
                      { key: "plan" as SortKey, label: "Plan" },
                      { key: null, label: "Status" },
                      { key: "memberCount" as SortKey, label: "Members" },
                      { key: "coachCount" as SortKey, label: "Coaches" },
                      { key: "contractCount" as SortKey, label: "Contracts" },
                      { key: null, label: "Activity (14d)" },
                      { key: "createdAt" as SortKey, label: "Signed Up" },
                    ].map((col) => (
                      <th
                        key={col.label}
                        className={`text-left px-4 py-3 text-xs font-medium text-gray-500 ${col.key ? "cursor-pointer hover:text-gray-300 select-none" : ""}`}
                        onClick={() => col.key && toggleSort(col.key)}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {col.key && <SortIcon k={col.key} />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingOrgs ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading organizations…</td>
                    </tr>
                  ) : sorted.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No organizations found</td>
                    </tr>
                  ) : sorted.map((o) => (
                    <tr key={o.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <Link
                          href={`/admin/orgs/${o.id}`}
                          className="text-white hover:text-blue-400 font-medium transition-colors"
                        >
                          {o.name}
                        </Link>
                        {(o.division || o.conference) && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {[o.division, o.conference].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${planBadgeClass(o.plan)}`}>
                          {planBadgeLabel(o.plan)}
                        </span>
                      </td>
                      <td className="px-4 py-3">{statusBadge(o)}</td>
                      <td className="px-4 py-3 text-gray-300">{o.memberCount}</td>
                      <td className="px-4 py-3 text-gray-300">{o.coachCount}</td>
                      <td className="px-4 py-3 text-gray-300">{o.contractCount}</td>
                      <td className="px-4 py-3"><Sparkline data={activity[String(o.id)] ?? []} /></td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "revenue" && (
          <div className="space-y-4">
            {revenue?.stripeError && (
              <div className="bg-yellow-950/40 border border-yellow-800 rounded-xl px-4 py-3 text-sm text-yellow-400">
                Stripe data unavailable — showing database-only metrics.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">New Sign-ups This Month</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-white">{revenue?.newThisMonth ?? "—"}</span>
                  {revenue && (
                    <span className={`text-xs mb-0.5 flex items-center gap-0.5 ${(revenue.newThisMonth ?? 0) >= (revenue.newLastMonth ?? 0) ? "text-green-400" : "text-red-400"}`}>
                      {(revenue.newThisMonth ?? 0) >= (revenue.newLastMonth ?? 0)
                        ? <ArrowUpRight className="h-3 w-3" />
                        : <ArrowDownRight className="h-3 w-3" />}
                      {revenue.newLastMonth} last mo.
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Total Organizations</p>
                <span className="text-2xl font-bold text-white">{revenue?.totalOrgs ?? "—"}</span>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Cancellations This Month</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold text-white">{revenue?.cancellationsThisMonth ?? "—"}</span>
                  {revenue && (
                    <span className={`text-xs mb-0.5 flex items-center gap-0.5 ${(revenue.cancellationsThisMonth ?? 0) <= (revenue.cancellationsLastMonth ?? 0) ? "text-green-400" : "text-red-400"}`}>
                      {revenue.cancellationsLastMonth} last mo.
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-white">Monthly Revenue (12 months)</h3>
                </div>
                {loadingRevenue ? (
                  <div className="h-48 flex items-center justify-center text-gray-500 text-sm">Loading…</div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={revenue?.monthlyRevenueTrend ?? []} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                      <XAxis dataKey="month" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", borderRadius: 8 }}
                        labelStyle={{ color: "#e5e7eb" }}
                        formatter={(v: number) => [`$${v.toLocaleString()}`, "Revenue"]}
                      />
                      <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4">Plan Distribution</h3>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={2}>
                        {pieData.map((d, i) => (
                          <Cell key={i} fill={planColor(d.planKey)} />
                        ))}
                      </Pie>
                      <Legend
                        formatter={(v) => <span style={{ color: "#9ca3af", fontSize: 12 }}>{v}</span>}
                      />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", borderRadius: 8 }}
                        formatter={(v: number) => [v, "Orgs"]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-44 flex items-center justify-center text-gray-500 text-sm">No data</div>
                )}
                {revenue && (
                  <div className="mt-2 space-y-1.5">
                    {Object.entries(revenue.planCounts).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: planColor(k) }} />
                          <span className="text-gray-400">{getPlanBadgeStyle(k).label}</span>
                        </div>
                        <span className="text-gray-300 font-medium">{v ?? 0}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
